import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Settings2 } from 'lucide-react';
import {
  getCurrentTimeInBothZones,
  getClientTimezone,
  getAssistantTimezone,
  getClientLabel,
  getAssistantLabel,
  setClientTimezone,
  setAssistantTimezone,
  setClientLabel,
  setAssistantLabel,
  getTimeZoneOptions,
} from '@/utils/timezoneHelper';

const timeZoneOptions = getTimeZoneOptions();

export default function TimezoneBar() {
  const { t } = useTranslation();
  const [time, setTime] = useState(getCurrentTimeInBothZones());
  const [showSettings, setShowSettings] = useState(false);
  const [clientTz, setClientTzState] = useState(getClientTimezone());
  const [assistantTz, setAssistantTzState] = useState(getAssistantTimezone());
  const [clientLabel, setClientLabelState] = useState(getClientLabel());
  const [assistantLabel, setAssistantLabelState] = useState(getAssistantLabel());
  const [filter1, setFilter1] = useState('');
  const [filter2, setFilter2] = useState('');

  useEffect(() => {
    const t = setInterval(() => setTime(getCurrentTimeInBothZones()), 1000);
    return () => clearInterval(t);
  }, []);

  const saveSettings = () => {
    setClientTimezone(clientTz);
    setAssistantTimezone(assistantTz);
    setClientLabel(clientLabel);
    setAssistantLabel(assistantLabel);
    setTime(getCurrentTimeInBothZones());
    setShowSettings(false);
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/80 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-blue-800">
            <Clock className="h-4 w-4" />
            <span className="font-medium">{t('timezone_label')}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded bg-white px-2 py-1 font-mono text-blue-900 shadow-sm">
              {getClientLabel()}: {time.client}
            </span>
            <span className="text-blue-600">·</span>
            <span className="rounded bg-white px-2 py-1 font-mono text-blue-900 shadow-sm">
              {getAssistantLabel()}: {time.assistant}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowSettings((s) => !s)}
          className="flex items-center gap-1 rounded border border-blue-300 bg-white px-2 py-1 text-blue-700 hover:bg-blue-100"
        >
          <Settings2 className="h-3.5 w-3.5" />
          {t('timezone_configure')}
        </button>
      </div>

      {showSettings && (
        <div className="mt-3 rounded border border-blue-200 bg-white p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('timezone_zone1_label')}
              </label>
              <input
                type="text"
                value={clientLabel}
                onChange={(e) => setClientLabelState(e.target.value)}
                placeholder={t('timezone_zone1_placeholder')}
                className="mb-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder={t('timezone_search_placeholder')}
                value={filter1}
                onChange={(e) => setFilter1(e.target.value)}
                className="mb-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <select
                value={clientTz}
                onChange={(e) => setClientTzState(e.target.value)}
                className="max-h-40 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                size={6}
              >
                {timeZoneOptions
                  .filter((opt) => {
                    if (opt.value === clientTz) return true;
                    if (!filter1) return true;
                    const q = filter1.toLowerCase();
                    return opt.value.toLowerCase().includes(q) || opt.label.toLowerCase().includes(q);
                  })
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('timezone_zone2_label')}
              </label>
              <input
                type="text"
                value={assistantLabel}
                onChange={(e) => setAssistantLabelState(e.target.value)}
                placeholder={t('timezone_zone2_placeholder')}
                className="mb-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <input
                type="text"
                placeholder={t('timezone_search_placeholder')}
                value={filter2}
                onChange={(e) => setFilter2(e.target.value)}
                className="mb-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <select
                value={assistantTz}
                onChange={(e) => setAssistantTzState(e.target.value)}
                className="max-h-40 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                size={6}
              >
                {timeZoneOptions
                  .filter((opt) => {
                    if (opt.value === assistantTz) return true;
                    if (!filter2) return true;
                    const q = filter2.toLowerCase();
                    return opt.value.toLowerCase().includes(q) || opt.label.toLowerCase().includes(q);
                  })
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {t('timezone_help')}
          </p>
          <button
            type="button"
            onClick={saveSettings}
            className="mt-2 rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
          >
            {t('timezone_save')}
          </button>
        </div>
      )}
    </div>
  );
}
